import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Ban, Building2, CalendarRange, FileKey, History, Pencil, RefreshCw, RotateCcw, Trash2, TriangleAlert, User, X,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { StatusPill } from '@/components/StatusPill';
import { useLabels } from '@/contexts/WorkspaceContext';
import { todayYmd } from '@/utils/date';
import { LEASE_PAYMENTS, Lease, LeasePayments, UpdateLeaseInput, realEstateService } from '@/services/real-estate.service';
import { chequesLabel } from './LeaseFormModal';
import {
  LEASE_PHASE_LABEL, LEASE_PHASE_VARIANT, RENT_PERIOD_LABEL, addDaysYmd, addYearsYmd, formatDay, formatPrice, placeLine, unitSpecs,
  useRealEstateAccess, ymdOf,
} from './shared';

type Panel = 'renew' | 'end' | 'edit' | null;
const END_REASONS = ['Moved out', 'Non-payment', 'Unit sold', 'Mutual agreement'];

interface Props {
  leaseUid: string | null;
  onClose: () => void;
  /** Something changed — refresh the list and tiles. */
  onChanged: () => void;
  /** Show another lease (the renewal, or the one it renewed). */
  onOpen: (leaseUid: string) => void;
}

const label = 'text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted';

function daysText(l: Lease): string {
  if (l.status !== 'active') return '';
  if (l.phase === 'upcoming') return 'Not started yet';
  if (l.daysToEnd < 0) return `Ended ${Math.abs(l.daysToEnd)} ${Math.abs(l.daysToEnd) === 1 ? 'day' : 'days'} ago — still marked active`;
  if (l.daysToEnd === 0) return 'Ends today';
  return `${l.daysToEnd} ${l.daysToEnd === 1 ? 'day' : 'days'} left`;
}

export default function LeaseDrawer({ leaseUid, onClose, onChanged, onOpen }: Props) {
  const t = useLabels();
  const { contactLinks } = useRealEstateAccess();
  const [lease, setLease] = useState<Lease | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<'missing' | 'error' | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Panel fields
  const [endDate, setEndDate] = useState('');
  const [rent, setRent] = useState('');
  const [payments, setPayments] = useState<LeasePayments>(1);
  const [deposit, setDeposit] = useState('');
  const [registration, setRegistration] = useState('');
  const [notes, setNotes] = useState('');
  const [endStatus, setEndStatus] = useState<'ended' | 'terminated'>('ended');
  const [endedOn, setEndedOn] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async (uid: string) => {
    setLoading(true);
    setFailed(null);
    try {
      setLease(await realEstateService.getLease(uid, { expectedStatuses: [404] }));
    } catch (e: unknown) {
      setFailed((e as { response?: { status?: number } })?.response?.status === 404 ? 'missing' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLease(null);
    setPanel(null);
    if (leaseUid) void load(leaseUid);
  }, [leaseUid, load]);

  useEffect(() => {
    if (!leaseUid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [leaseUid, onClose]);

  if (!leaseUid) return null;

  const openPanel = (p: Panel) => {
    if (!lease) return;
    setPanel(p);
    if (p === 'renew') {
      setEndDate(addYearsYmd(ymdOf(lease.endDate), 1));
      setRent(String(lease.rentAmount));
      setPayments((LEASE_PAYMENTS as readonly number[]).includes(lease.payments) ? (lease.payments as LeasePayments) : 1);
      setRegistration('');
    }
    if (p === 'edit') {
      setEndDate(ymdOf(lease.endDate));
      setRent(String(lease.rentAmount));
      setPayments((LEASE_PAYMENTS as readonly number[]).includes(lease.payments) ? (lease.payments as LeasePayments) : 1);
      setDeposit(lease.securityDeposit !== null && lease.securityDeposit !== undefined ? String(lease.securityDeposit) : '');
      setRegistration(lease.registrationNumber ?? '');
      setNotes(lease.notes ?? '');
    }
    if (p === 'end') {
      setEndStatus(lease.daysToEnd <= 0 ? 'ended' : 'terminated');
      setEndedOn(todayYmd());
      setReason('');
    }
  };

  const run = async (key: string, fn: () => Promise<Lease | void>, success: string) => {
    setBusy(key);
    try {
      const next = await fn();
      toast.success(success);
      setPanel(null);
      onChanged();
      if (next) {
        if (next.leaseUid !== lease?.leaseUid) onOpen(next.leaseUid);
        else setLease(next);
      }
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Could not save — check your connection');
    } finally {
      setBusy(null);
    }
  };

  const renew = () => {
    if (!lease) return;
    const newStart = addDaysYmd(ymdOf(lease.endDate), 1);
    if (!endDate || endDate <= newStart) { toast.error(`The new term must end after ${formatDay(`${newStart}T00:00:00Z`)}`); return; }
    const amount = Number(rent);
    if (!(amount >= 1)) { toast.error('Enter the new rent'); return; }
    void run('renew', () => realEstateService.renewLease(lease.leaseUid, {
      endDate,
      rentAmount: amount !== lease.rentAmount ? amount : undefined,
      payments: payments !== lease.payments ? payments : undefined,
      registrationNumber: registration.trim() || undefined,
    }), `Renewed until ${formatDay(`${endDate}T00:00:00Z`)}`);
  };

  const end = () => {
    if (!lease) return;
    const unitWord = lease.unit?.label ?? 'The unit';
    void run('end', () => realEstateService.endLease(lease.leaseUid, {
      status: endStatus,
      endedAt: endedOn || undefined,
      reason: reason.trim() || undefined,
    }), `${t('leases', 'singular')} ${endStatus === 'ended' ? 'ended' : 'terminated'} — ${unitWord} is available again`);
  };

  const saveEdit = () => {
    if (!lease) return;
    const patch: UpdateLeaseInput = {};
    if (endDate !== ymdOf(lease.endDate)) {
      if (!endDate || endDate <= ymdOf(lease.startDate)) { toast.error('The lease must end after it starts'); return; }
      patch.endDate = endDate;
    }
    const amount = Number(rent);
    if (!(amount >= 1)) { toast.error('Enter the rent'); return; }
    if (amount !== lease.rentAmount) patch.rentAmount = amount;
    if (payments !== lease.payments) patch.payments = payments;
    const dep = deposit.trim() ? Number(deposit) : null;
    if (dep !== null && !(dep >= 0)) { toast.error('The deposit must be 0 or more'); return; }
    if (dep !== (lease.securityDeposit ?? null)) patch.securityDeposit = dep;
    if (registration.trim() !== (lease.registrationNumber ?? '')) patch.registrationNumber = registration.trim();
    if (notes.trim() !== (lease.notes ?? '').trim()) patch.notes = notes.trim();
    if (Object.keys(patch).length === 0) { setPanel(null); return; }
    void run('edit', () => realEstateService.updateLease(lease.leaseUid, patch), 'Lease updated');
  };

  const remove = () => {
    if (!lease) return;
    const active = lease.status === 'active';
    if (!window.confirm(`Delete this ${t('leases', 'singular').toLowerCase()} for ${lease.tenant?.name ?? 'the tenant'}?${active ? ` ${lease.unit?.label ?? 'The unit'} becomes available again.` : ''} This can’t be undone.`)) return;
    setBusy('delete');
    realEstateService.removeLease(lease.leaseUid)
      .then(() => { toast.success(`${t('leases', 'singular')} deleted`); onChanged(); onClose(); })
      .catch((e: unknown) => { if (!(e as { response?: unknown })?.response) toast.error('Could not delete — check your connection'); })
      .finally(() => setBusy(null));
  };

  const active = lease?.status === 'active';
  const now = Date.now();
  const nextDue = lease?.schedule.items.find((i) => new Date(i.dueDate).getTime() >= now - 86_400_000);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={lease ? `${t('leases', 'singular')} for ${lease.tenant?.name ?? 'tenant'}` : t('leases', 'singular')}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-[520px] flex-col border-l border-border bg-surface shadow-elevated animate-fade-in">
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          {lease ? (
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate font-display text-lg font-bold tracking-tight text-text-primary">{lease.tenant?.name ?? 'Tenant removed'}</h2>
                <StatusPill status={lease.phase} label={LEASE_PHASE_LABEL[lease.phase]} variant={LEASE_PHASE_VARIANT[lease.phase]} />
              </div>
              <p className="mt-1 truncate text-xs text-text-muted">
                {lease.unit?.label ?? 'Unit removed'}{lease.property ? ` · ${lease.property.name}` : ''}
                {lease.registrationNumber ? ` · Reg. ${lease.registrationNumber}` : ''}
              </p>
            </div>
          ) : (
            <div className="skeleton h-9 w-48 rounded-lg" />
          )}
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {failed ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger"><TriangleAlert size={22} /></span>
              <p className="text-sm font-semibold text-text-primary">{failed === 'missing' ? `This ${t('leases', 'singular').toLowerCase()} no longer exists` : `Couldn’t load this ${t('leases', 'singular').toLowerCase()}`}</p>
              {failed === 'error' && <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={() => void load(leaseUid)}>Retry</Button>}
            </div>
          ) : !lease || loading ? (
            <div className="space-y-3 p-5"><div className="skeleton h-24 rounded-xl" /><div className="skeleton h-40 rounded-xl" /><div className="skeleton h-28 rounded-xl" /></div>
          ) : (
            <div className="space-y-5 p-5">
              {active && lease.phase === 'past_end' && (
                <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-[13px] text-danger">
                  <TriangleAlert size={15} className="mt-0.5 shrink-0" />
                  <span>The end date has passed but the {t('leases', 'singular').toLowerCase()} is still active. Renew it or end it so the unit’s status is right.</span>
                </div>
              )}

              <section className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border p-3">
                  <p className={clsx(label, 'mb-1.5 flex items-center gap-1')}><User size={11} /> Tenant</p>
                  {lease.tenant ? (
                    <>
                      {contactLinks ? (
                        <Link to={`/contacts/${lease.tenant.contactUid}`} className="block truncate text-[13px] font-semibold text-text-primary hover:text-primary hover:underline">{lease.tenant.name}</Link>
                      ) : <p className="truncate text-[13px] font-semibold text-text-primary">{lease.tenant.name}</p>}
                      {lease.tenant.phone && <a href={`tel:${lease.tenant.phone}`} className="block truncate text-2xs tabular-nums text-text-muted hover:text-primary">{lease.tenant.phone}</a>}
                      {lease.tenant.email && <p className="truncate text-2xs text-text-muted">{lease.tenant.email}</p>}
                    </>
                  ) : <p className="text-xs text-text-muted">Contact removed</p>}
                </div>
                <div className="rounded-xl border border-border p-3">
                  <p className={clsx(label, 'mb-1.5 flex items-center gap-1')}><Building2 size={11} /> Unit</p>
                  <Link to={`/properties/${lease.propertyUid}`} className="block truncate text-[13px] font-semibold text-text-primary hover:text-primary hover:underline">
                    {lease.unit?.label ?? 'Unit removed'}
                  </Link>
                  <p className="truncate text-2xs text-text-muted">{lease.property?.name}</p>
                  <p className="truncate text-2xs text-text-muted">{[lease.unit ? unitSpecs(lease.unit) : null, placeLine(lease.property)].filter(Boolean).join(' · ')}</p>
                </div>
              </section>

              <section className="rounded-2xl border border-border p-4">
                <p className={clsx(label, 'mb-3 flex items-center gap-1')}><CalendarRange size={11} /> Term &amp; rent</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]">
                  <div><dt className="text-2xs text-text-muted">Starts</dt><dd className="font-medium text-text-primary">{formatDay(lease.startDate)}</dd></div>
                  <div><dt className="text-2xs text-text-muted">Ends</dt><dd className="font-medium text-text-primary">{formatDay(lease.endDate)}</dd></div>
                  <div><dt className="text-2xs text-text-muted">Rent</dt><dd className="font-medium text-text-primary">{formatPrice(lease.rentAmount, lease.currency)} <span className="font-normal text-text-muted">{RENT_PERIOD_LABEL[lease.rentPeriod]}</span></dd></div>
                  <div><dt className="text-2xs text-text-muted">Payments</dt><dd className="font-medium text-text-primary">{chequesLabel(lease.payments)}</dd></div>
                  <div><dt className="text-2xs text-text-muted">Security deposit</dt><dd className="font-medium text-text-primary">{formatPrice(lease.securityDeposit, lease.currency)}</dd></div>
                  <div><dt className="text-2xs text-text-muted">Term</dt><dd className="font-medium text-text-primary">{lease.schedule.termMonths} {lease.schedule.termMonths === 1 ? 'month' : 'months'}</dd></div>
                </dl>
                {daysText(lease) && (
                  <p className={clsx('mt-3 text-xs font-medium', lease.phase === 'past_end' ? 'text-danger' : lease.phase === 'expiring' ? 'text-warning' : 'text-text-secondary')}>{daysText(lease)}</p>
                )}
                {lease.status !== 'active' && (
                  <p className="mt-3 text-xs text-text-secondary">
                    {lease.status === 'terminated' ? 'Terminated' : 'Ended'} {formatDay(lease.endedAt)}{lease.endReason ? ` — ${lease.endReason}` : ''}
                  </p>
                )}
                {lease.notes && <p className="mt-3 whitespace-pre-line rounded-lg bg-surface-alt px-3 py-2 text-xs text-text-secondary">{lease.notes}</p>}
              </section>

              {(lease.renewedFromLeaseUid || lease.renewedToLeaseUid) && (
                <section className="flex flex-wrap gap-2">
                  {lease.renewedFromLeaseUid && (
                    <button type="button" onClick={() => lease.renewedFromLeaseUid && onOpen(lease.renewedFromLeaseUid)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium text-text-secondary hover:border-primary/50 hover:text-primary">
                      <History size={12} /> Previous term
                    </button>
                  )}
                  {lease.renewedToLeaseUid && (
                    <button type="button" onClick={() => lease.renewedToLeaseUid && onOpen(lease.renewedToLeaseUid)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium text-text-secondary hover:border-primary/50 hover:text-primary">
                      <RotateCcw size={12} /> Renewed term
                    </button>
                  )}
                </section>
              )}

              <section>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <p className={clsx(label, 'flex items-center gap-1')}><FileKey size={11} /> Payment schedule</p>
                  <span className="text-xs text-text-muted">Total <span className="font-semibold text-text-primary">{formatPrice(lease.schedule.totalRent, lease.currency)}</span></span>
                </div>
                <ol className="overflow-hidden rounded-xl border border-border">
                  {lease.schedule.items.map((p, i) => {
                    const past = new Date(p.dueDate).getTime() < now - 86_400_000;
                    const isNext = active && nextDue === p;
                    return (
                      <li key={`${p.dueDate}-${i}`} className={clsx('flex items-center justify-between gap-3 border-b border-border px-3 py-2 text-[13px] last:border-b-0', isNext && 'bg-primary-soft/60')}>
                        <span className="flex items-center gap-2">
                          <span className="w-5 text-2xs tabular-nums text-text-muted">{i + 1}</span>
                          <span className={clsx('tabular-nums', past ? 'text-text-muted' : 'text-text-primary')}>{formatDay(p.dueDate)}</span>
                          {isNext && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-on-primary">Next</span>}
                        </span>
                        <span className={clsx('font-semibold tabular-nums', past ? 'text-text-muted' : 'text-text-primary')}>{formatPrice(p.amount, lease.currency)}</span>
                      </li>
                    );
                  })}
                </ol>
                <p className="mt-1.5 text-2xs text-text-muted">Due dates spread evenly over the term. Payments aren’t tracked here.</p>
              </section>

              {active && panel === null && (
                <section className="flex flex-wrap gap-2 border-t border-border pt-4">
                  <Button size="sm" iconLeft={<RotateCcw size={14} />} onClick={() => openPanel('renew')}>Renew</Button>
                  <Button size="sm" variant="secondary" iconLeft={<Pencil size={14} />} onClick={() => openPanel('edit')}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-danger hover:text-danger" iconLeft={<Ban size={14} />} onClick={() => openPanel('end')}>End or terminate</Button>
                </section>
              )}

              {panel === 'renew' && (
                <section className="rounded-2xl border border-primary/30 p-4">
                  <h3 className="text-sm font-semibold text-text-primary">Renew for another term</h3>
                  <p className="mt-0.5 text-xs text-text-muted">The new term starts {formatDay(`${addDaysYmd(ymdOf(lease.endDate), 1)}T00:00:00Z`)}. This one closes as renewed.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <label className="block text-xs font-medium text-text-secondary">New end date
                      <Input className="mt-1" type="date" value={endDate} min={addDaysYmd(ymdOf(lease.endDate), 2)} onChange={(e) => setEndDate(e.target.value)} />
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Rent ({RENT_PERIOD_LABEL[lease.rentPeriod]})
                      <Input className="mt-1" type="number" inputMode="decimal" min={1} step="any" value={rent} onChange={(e) => setRent(e.target.value)} />
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Payments
                      <Select className="mt-1 h-10" value={String(payments)} onChange={(e) => setPayments(Number(e.target.value) as LeasePayments)}>
                        {LEASE_PAYMENTS.map((p) => <option key={p} value={p}>{chequesLabel(p)}</option>)}
                      </Select>
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Registration no.
                      <Input className="mt-1" value={registration} maxLength={60} onChange={(e) => setRegistration(e.target.value)} placeholder="New number" />
                    </label>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPanel(null)} disabled={!!busy}>Back</Button>
                    <Button size="sm" loading={busy === 'renew'} onClick={renew}>Renew</Button>
                  </div>
                </section>
              )}

              {panel === 'edit' && (
                <section className="rounded-2xl border border-border p-4">
                  <h3 className="text-sm font-semibold text-text-primary">Edit {t('leases', 'singular').toLowerCase()}</h3>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <label className="block text-xs font-medium text-text-secondary">End date
                      <Input className="mt-1" type="date" value={endDate} min={addDaysYmd(ymdOf(lease.startDate), 1)} onChange={(e) => setEndDate(e.target.value)} />
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Rent ({RENT_PERIOD_LABEL[lease.rentPeriod]})
                      <Input className="mt-1" type="number" inputMode="decimal" min={1} step="any" value={rent} onChange={(e) => setRent(e.target.value)} />
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Payments
                      <Select className="mt-1 h-10" value={String(payments)} onChange={(e) => setPayments(Number(e.target.value) as LeasePayments)}>
                        {LEASE_PAYMENTS.map((p) => <option key={p} value={p}>{chequesLabel(p)}</option>)}
                      </Select>
                    </label>
                    <label className="block text-xs font-medium text-text-secondary">Security deposit
                      <Input className="mt-1" type="number" inputMode="decimal" min={0} step="any" value={deposit} onChange={(e) => setDeposit(e.target.value)} />
                    </label>
                    <label className="col-span-2 block text-xs font-medium text-text-secondary">Registration no.
                      <Input className="mt-1" value={registration} maxLength={60} onChange={(e) => setRegistration(e.target.value)} />
                    </label>
                    <label className="col-span-2 block text-xs font-medium text-text-secondary">Notes
                      <Textarea className="mt-1" rows={2} maxLength={4000} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </label>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPanel(null)} disabled={!!busy}>Back</Button>
                    <Button size="sm" loading={busy === 'edit'} onClick={saveEdit}>Save</Button>
                  </div>
                </section>
              )}

              {panel === 'end' && (
                <section className="rounded-2xl border border-danger/30 p-4">
                  <h3 className="text-sm font-semibold text-text-primary">End this {t('leases', 'singular').toLowerCase()}</h3>
                  <p className="mt-0.5 text-xs text-text-muted">{lease.unit?.label ?? 'The unit'} goes back to available.</p>
                  <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="How it ends">
                    {(['ended', 'terminated'] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        role="radio"
                        aria-checked={endStatus === s}
                        onClick={() => setEndStatus(s)}
                        className={clsx('rounded-xl border px-3 py-2 text-left text-xs transition-colors', endStatus === s ? 'border-danger/50 bg-danger-soft text-danger' : 'border-border text-text-secondary hover:border-border-strong')}
                      >
                        <span className="block font-semibold">{s === 'ended' ? 'Ended' : 'Terminated'}</span>
                        <span className="block text-2xs opacity-80">{s === 'ended' ? 'Ran its course' : 'Stopped early'}</span>
                      </button>
                    ))}
                  </div>
                  <label className="mt-3 block text-xs font-medium text-text-secondary">Date
                    <Input className="mt-1" type="date" value={endedOn} onChange={(e) => setEndedOn(e.target.value)} />
                  </label>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {END_REASONS.map((r) => (
                      <button key={r} type="button" onClick={() => setReason(r)} className={clsx('rounded-full border px-2.5 py-1 text-xs font-medium', reason === r ? 'border-danger bg-danger-soft text-danger' : 'border-border text-text-secondary hover:border-border-strong')}>
                        {r}
                      </button>
                    ))}
                  </div>
                  <Textarea className="mt-2" rows={2} maxLength={400} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or type a reason (optional)" />
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPanel(null)} disabled={!!busy}>Keep it active</Button>
                    <Button size="sm" variant="danger" loading={busy === 'end'} onClick={end}>{endStatus === 'ended' ? 'End lease' : 'Terminate lease'}</Button>
                  </div>
                </section>
              )}

              <section className="border-t border-border pt-4">
                <Button size="sm" variant="ghost" className="text-danger hover:text-danger" iconLeft={<Trash2 size={14} />} loading={busy === 'delete'} disabled={!!busy && busy !== 'delete'} onClick={remove}>
                  Delete {t('leases', 'singular').toLowerCase()}
                </Button>
              </section>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
